/**
 * useAuthProviders Hook
 * Standardized API hooks for authentication provider configuration
 *
 * Refactored: Phase 1 - Factory Pattern
 * Pattern: Uses createQueryHook and createMutationHook factories for code reduction
 */

import type { AuthProviderResponse } from '../../api/data-contracts';
import { log } from '../../utils/errors/logger';
import { QUERY_STALE_TIME } from '../../constants';
import { createQueryHook, createMutationHook } from './factories';

// --- Types ---

interface AuthProviderConfig {
  provider_type: string;
  client_id?: string;
  client_secret?: string;
  tenant_id?: string;
  domain?: string;
  redirect_uri?: string;
  scopes?: string[];
  is_primary?: boolean;
  enabled?: boolean;
}

interface SetPrimaryProviderRequest {
  provider_type: string;
}

interface PublicAuthProvider {
  provider_type: string;
  is_primary: boolean;
  login_url: string;
  display_name: string;
  method: 'form' | 'redirect';
}

interface PublicAuthProvidersResponse {
  providers: PublicAuthProvider[];
  total: number;
}

interface EntraConfig {
  tenant_id: string;
  client_id: string;
  redirect_uri?: string;
}

// --- Query Hooks ---

/**
 * Get public auth providers for login (no authentication required)
 */
export const usePublicAuthProviders = createQueryHook<PublicAuthProvidersResponse, void>({
  queryKey: () => ['auth-providers', 'public'] as const,
  queryFn: (apiClient) =>
    apiClient.getAvailableAuthProvidersApiAuthProvidersGet().then(response => response.data),
  logContext: 'GET /api/auth-providers',
  staleTime: QUERY_STALE_TIME.LONG,
  onSuccess: (data) => {
    log.api.response('GET', '/api/auth-providers', 200, {
      providerCount: data?.providers?.length ?? 0
    });
  },
});

/**
 * Get Entra configuration for MSAL initialization (no authentication required)
 */
export const useEntraConfig = createQueryHook<EntraConfig, void>({
  queryKey: () => ['auth-providers', 'entra-config'] as const,
  queryFn: (apiClient) =>
    apiClient.getEntraConfigApiAuthEntraConfigGet().then(response => response.data),
  logContext: 'GET /api/auth/entra/config',
  staleTime: QUERY_STALE_TIME.LONG,
  onSuccess: (data) => {
    log.api.response('GET', '/api/auth/entra/config', 200, {
      tenantId: data?.tenant_id
    });
  },
});

/**
 * Get auth providers for system
 */
export const useAuthProviders = createQueryHook<AuthProviderResponse[], void>({
  queryKey: () => ['auth-providers'] as const,
  queryFn: (apiClient) =>
    apiClient.getMyOrganizationApiOrganizationsMyGet().then(response => response.data?.auth_providers ?? []),
  logContext: 'GET /api/organizations/my',
  staleTime: QUERY_STALE_TIME.LONG,
  onSuccess: (data) => {
    log.api.response('GET', '/api/organizations/my', 200, {
      providerCount: data?.length ?? 0
    });
  },
});

// --- Mutation Hooks ---

/**
 * Save auth provider configuration
 */
export const useSaveAuthProvider = createMutationHook<
  AuthProviderResponse,
  { providerType: string; config: AuthProviderConfig }
>({
  mutationFn: (apiClient, { providerType, config }) =>
    apiClient.request<AuthProviderResponse>({
      path: `/api/auth-providers/organizations/current/auth-providers/${providerType}`,
      method: 'PUT',
      body: config,
      type: 'application/json',
      secure: true
    }).then(response => response.data),
  invalidateQueries: [
    ['auth-providers'],
    ['system'],
  ],
  logContext: 'PUT /api/auth-providers/organizations/current/auth-providers/:providerType',
  logMethod: 'PUT',
  onSuccess: (_data, variables) => {
    log.user.action('auth_provider_saved', 'useAuthProviders', {
      providerType: variables.providerType
    });
  },
});

/**
 * Set primary auth provider
 */
export const useSetPrimaryAuthProvider = createMutationHook<
  AuthProviderResponse,
  SetPrimaryProviderRequest
>({
  mutationFn: (apiClient, request) =>
    apiClient.request<AuthProviderResponse>({
      path: '/api/auth-providers/organizations/current/auth-providers/primary',
      method: 'PUT',
      body: request,
      type: 'application/json',
      secure: true
    }).then(response => response.data),
  invalidateQueries: [
    ['auth-providers'],
    ['system'],
  ],
  logContext: 'PUT /api/auth-providers/organizations/current/auth-providers/primary',
  logMethod: 'PUT',
  onSuccess: (_data, variables) => {
    log.user.action('primary_auth_provider_set', 'useAuthProviders', {
      providerType: variables.provider_type
    });
  },
});
